import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskedLines } from "@/components/motion/Reveal";
import { trackEvent } from "@/lib/analytics";
import { directionsUrl } from "@/data/social";
import { uns } from "@/lib/uns";

const ease = [0.22, 0.61, 0.36, 1];
const heroPhoto = uns("photo-1503376780353-7e6692767b70", { w: 900, widths: [400, 640, 900] });

/**
 * Signature editorial hero.
 * - Line-by-line masked reveal of the display headline
 * - Massive numeric "1700" address marker with parallax
 * - Floating shop-photo tile with 3D tilt on scroll
 * - Ambient turquoise glow + grain overlay
 */
export default function HeroKinetic() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bigY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const bigOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.15]);
  const photoY = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);
  const photoRotate = useTransform(scrollYProgress, [0, 1], [0, -3.2]);
  const glowY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section ref={ref} className="abc-hero-k" id="top">
      <motion.div className="abc-hero-k-glow" style={{ y: glowY }} aria-hidden="true" />
      <div className="abc-hero-k-grain" aria-hidden="true" />

      <div className="container abc-hero-k-inner">
        <div className="abc-hero-k-topline">
          <span className="abc-mono-label abc-hero-k-crumbs">
            <span className="abc-hero-k-dot" /> Est. Birmingham AL · 1700 31st St SW
          </span>
          <span className="abc-mono-label abc-hero-k-crumbs abc-hero-k-hours">
            Open Mon–Sat · 08:00–16:30
          </span>
        </div>

        <h1 className="abc-hero-k-title" data-testid="hero-title">
          <MaskedLines
            lines={["Dependable", <>automotive <em>care</em>,</>, "priced honestly."]}
            stagger={0.14}
            delay={0.15}
          />
        </h1>

        <motion.p
          className="abc-hero-k-lede"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease, delay: 0.85 }}
        >
          A Birmingham shop built on diagnostics, plain-English recommendations,
          and repairs that hold — for daily drivers, work trucks, and local fleets.
        </motion.p>

        <motion.div
          className="abc-hero-k-actions"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease, delay: 1.0 }}
        >
          <a href="#schedule" className="abc-btn-ink" data-testid="hero-schedule-btn">
            <span>Book an appointment</span>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </a>
          <a
            href="tel:2054051238"
            className="abc-btn-ghost"
            data-testid="hero-call-btn"
            onClick={() => trackEvent("Contact", { source: "hero" })}
          >
            <span>Call 205 · 405 · 1238</span>
          </a>
          <a
            href={directionsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="abc-btn-ghost abc-btn-ghost-quiet"
            data-testid="hero-directions-btn"
            onClick={() => trackEvent("Contact", { source: "hero-directions" })}
          >
            <span>Directions ↗</span>
          </a>
        </motion.div>

        <motion.div
          className="abc-hero-k-meta-row"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 1.15 }}
        >
          <div className="abc-hero-k-meta">
            <span className="abc-mono-label">Since</span>
            <strong className="abc-hero-k-meta-value">2015</strong>
          </div>
          <div className="abc-hero-k-meta">
            <span className="abc-mono-label">Bays</span>
            <strong className="abc-hero-k-meta-value">06</strong>
          </div>
          <div className="abc-hero-k-meta">
            <span className="abc-mono-label">Same-Day</span>
            <strong className="abc-hero-k-meta-value">Availability</strong>
          </div>
          <div className="abc-hero-k-meta">
            <span className="abc-mono-label">Rating</span>
            <strong className="abc-hero-k-meta-value">★ 4.9<sup>/5</sup></strong>
          </div>
        </motion.div>

        <motion.figure
          className="abc-hero-k-photo"
          style={{ y: photoY, rotate: photoRotate }}
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, ease, delay: 0.55 }}
        >
          <img
            src={heroPhoto.src}
            srcSet={heroPhoto.srcSet}
            sizes="(min-width: 640px) 640px, 100vw"
            alt="Technician inspecting a vehicle in the Auto Basic Care service bay"
            loading="eager"
            decoding="async"
            fetchPriority="high"
          />
          <figcaption className="abc-hero-k-photo-cap">
            <span className="abc-mono-label">On the lift · Bay 03</span>
          </figcaption>
        </motion.figure>

        <motion.div
          className="abc-hero-k-big"
          style={{ y: bigY, opacity: bigOpacity }}
          aria-hidden="true"
        >
          1700
        </motion.div>

        <motion.a
          href="#schedule"
          className="abc-hero-k-scroll"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.4 }}
          aria-label="Scroll to schedule an appointment"
          data-testid="hero-scroll-cue"
        >
          <span className="abc-mono-label">Scroll</span>
          <span className="abc-hero-k-scroll-line" />
        </motion.a>
      </div>
    </section>
  );
}
